import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { HiOutlinePlus, HiOutlineSquares2X2, HiOutlineClock } from "react-icons/hi2";
import { HiOutlineArrowRight } from "react-icons/hi";
import { FaEye } from "react-icons/fa";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import ConfirmDeleteModal from "../../common/DeleteModel";
import { DiePurchaseOrderAPI } from "../../utlis/apiService";
import DiePurchaseOrderForm from "./DiePurchaseOrderForm";
import ErrorModal from "../../common/CustomModel";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";
import PendingInwardTab from "./PendingInwardTab";
import { RegistryHeader, Button as SharedButton } from "../../common/Shared";

const DiePurchaseOrderMaster = () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.userId;
    const today = new Date().toISOString()?.split("T")[0];
    const [activeTab, setActiveTab] = useState("po_history");
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorSubMessage, setErrorSubMessage] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [],
    });
    const [expandedRows, setExpandedRows] = useState({});
    const [expandedData, setExpandedData] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataList, setDataList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 50;
    const [hasMore, setHasMore] = useState(true);
    const isFetchingRef = useRef(false);
    const tableScrollRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    const [isInwardModalOpen, setIsInwardModalOpen] = useState(false);

    const handleInwardProcess = () => {
        if (selectedItems.length === 0) {
            notify("Please select at least one die to inward", "warning");
            return;
        }
        setIsInwardModalOpen(true);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await DiePurchaseOrderAPI.getAllWithPagination(
                currentPage,
                pageSize
            );
            if (currentPage === 1) {
                setDataList(res.data || []);
            } else {
                setDataList((prev) => [...prev, ...(res.data || [])]);
            }

            if (!res.data || res.data.length < pageSize) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            isFetchingRef.current = false;
        } catch (error) {
            notify("Error fetching purchase orders", "error");
        }
        setLoading(false);
    };

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;

        const onScroll = () => {
            if (loading || !hasMore || isFetchingRef.current) return;

            const { scrollTop, clientHeight, scrollHeight } = el;

            if (scrollHeight - scrollTop <= clientHeight + 100) {
                isFetchingRef.current = true;
                setCurrentPage((p) => p + 1);
            }
        };

        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, [loading, hasMore]);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const handleAdd = () => {
        setIsModelOpen(true);
        setIsEditing(false);
        setFormData(blankForm);
    };

    const handleEdit = async (id) => {
        setLoading(true);
        try {
            const res = await DiePurchaseOrderAPI.getById(id);
            if (res.data) {
                const d = res.data;
                setFormData({
                    po_No: d.po_No || d.PO_No || d.pO_No || d.id || d.Id || 0,
                    po_Date: (d.po_Date || d.PO_Date || d.pO_Date || today).split("T")[0],
                    supplier_Id: d.supplier_Id || d.Supplier_Id || d.supplier_ID || "",
                    delivery_Days: d.delivery_Days || d.Delivery_Days || "",
                    delivery_Date: (d.delivery_Date || d.Delivery_Date || "").split("T")[0],
                    created_By: d.created_By || d.Created_By || userId,
                    details: (d.details || d.Details || []).map(item => ({
                        trn_No: item.trn_No || item.Trn_No || 0,
                        dieNumber: item.dieNumber || item.DieNumber || "",
                        subDieNo: item.subDieNo || item.SubDieNo || "",
                        is_Bloster: item.is_Bloster || item.Is_Bloster || false,
                        bloster_No: item.bloster_No || item.Bloster_No || "",
                        orderWeight: item.orderWeight || item.OrderWeight || "",
                        profile_Name: item.profile_Name || item.Profile_Name || "",
                        series_Name: item.series_Name || item.Series_Name || "",
                        width_Height: item.width_Height || item.Width_Height || "",
                        range_Per: item.range_Per || item.Range_Per || "",
                        area_Sqmm: item.area_Sqmm || item.Area_Sqmm || "",
                        perimeter: item.perimeter || item.Perimeter || "",
                        is_Image_Upload: item.is_Image_Upload || item.Is_Image_Upload || false,
                        file: null,
                        is_Pdf_Upload: !!(item.file || item.File || item.is_Pdf_Upload || item.Is_Pdf_Upload),
                        die_Image: item.die_Image || item.Die_Image || "",
                        qty: item.qty || item.Qty || "",
                        rate: item.rate || item.Rate || "",
                        amount: item.amount || item.Amount || "",
                        gst_per: item.gst_Per || item.Gst_Per || item.gst_per || "",
                        gst_amount: item.gst_Amount || item.Gst_Amount || item.gst_amount || "",
                    }))
                });
                setIsEditing(true);
                setIsModelOpen(true);
            }
        } catch (error) {
            notify("Failed to fetch details", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleExpandItems = async (id, newState) => {
        const poNo = id;
        setExpandedRows(prev => ({ ...prev, [poNo]: newState }));

        if (newState && !expandedData[poNo]) {
            setLoadingDetails(prev => ({ ...prev, [poNo]: true }));
            try {
                const res = await DiePurchaseOrderAPI.getDiePOItems(poNo);
                if (res?.success) {
                    setExpandedData(prev => ({ ...prev, [poNo]: res.data }));
                } else {
                    notify(res?.message || "No items found", "warning");
                }
            } catch (err) {
                notify("Failed to load item details", "error");
            } finally {
                setLoadingDetails(prev => ({ ...prev, [poNo]: false }));
            }
        }
    };

    const handleDelete = (id) => {
        setSelectedId(id);
        setIsDeleteModalOpen(true);
    };

    const blankForm = {
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [{
            trn_No: 0,
            dieNumber: "",
            subDieNo: "",
            is_Bloster: false,
            bloster_No: "",
            file: null,
            is_Pdf_Upload: false,
            orderWeight: "",
            profile_Name: "",
            series_Name: "",
            width: "",
            height: "",
            die_Image: "",
            imageFile: null,
            is_Image_Upload: false,
            range_Per: "",
            area_Sqmm: "",
            perimeter: "",
            qty: "",
            rate: "",
            amount: "",
            gst_per: "",
            gst_amount: ""
        }],
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        try {
            await DiePurchaseOrderAPI.delete(selectedId);
            notify("Deleted successfully", "success");
            fetchData();
        } catch (error) {
            notify("Failed to delete record", "error");
        } finally {
            setLoading(false);
            setIsDeleteModalOpen(false);
            setSelectedId(null);
        }
    };

    const handlePrint = async (row) => {
        const poNo = row.pO_No || row.PO_No;
        try {
            const blob = await DiePurchaseOrderAPI.generatePdf(poNo);
            const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `DiePO_${poNo}.pdf`);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error) {
            notify("Failed to generate PDF", "error");
        }
    };

    const handleSubmit = async (submittedData) => {
        setLoading(true);
        try {
            await DiePurchaseOrderAPI.saveOrUpdate(submittedData);
            notify("Saved successfully", "success");
            setIsModelOpen(false);
            setFormData(blankForm);
            if (currentPage === 1) fetchData();
            else setCurrentPage(1);
        } catch (err) {
            notify("Save failed", "error");
            setErrorMessage("Submission failed");
            setErrorSubMessage("Please check your input or try again.");
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    if (isModelOpen) {
        return (
            <DiePurchaseOrderForm
                initialData={formData}
                key={JSON.stringify(formData)}
                onCancel={() => {
                    setIsModelOpen(false);
                    setFormData(blankForm);
                }}
                onSubmit={handleSubmit}
                isLoading={loading}
                isEditing={isEditing}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-white font-plus relative">
            {loading && <Loader centered />}
            <RegistryHeader
                title="Die Purchase Orders"
            >
                <div className="flex bg-gray-100 p-1 rounded-sm gap-1">
                    <button
                        onClick={() => setActiveTab("po_history")}
                        className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "po_history"
                            ? "bg-[#3F80F7] text-white  #3F80F7]/20"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
                            }`}
                    >
                        PO History
                    </button>
                    <button
                        onClick={() => setActiveTab("pending_inward")}
                        className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all flex items-center ${activeTab === "pending_inward"
                            ? "bg-[#3F80F7] text-white  #3F80F7]/20"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
                            }`}
                    >
                        Pending Inward
                    </button>
                </div>

                {activeTab === "po_history" && (
                    <>
                        <div className="relative w-full sm:w-80 group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
                                <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search PO No / Supplier..."
                                className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <MdClose size={18} />
                                </button>
                            )}
                        </div>

                        <SharedButton
                            onClick={handleAdd}
                            icon={HiOutlinePlus}
                            className="whitespace-nowrap"
                        >
                            Add New PO
                        </SharedButton>
                    </>
                )}
                {activeTab === "pending_inward" && (
                    <>
                        <div className="relative w-full sm:w-80 group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
                                <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search pending logs..."
                                className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm("")} className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors">
                                    <MdClose size={18} />
                                </button>
                            )}
                        </div>

                        <button
                            onClick={handleInwardProcess}
                            disabled={selectedItems.length === 0}
                            className={`px-8 py-2.5 font-black text-[10px] uppercase tracking-[3px] transition-all flex items-center gap-3 whitespace-nowrap border ${selectedItems.length > 0
                                ? "bg-[#3F80F7] text-white border-[#3F80F7] hover:bg-gray-800"
                                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                }`}
                        >
                            Inward Process {selectedItems.length > 0 && `(${selectedItems.length})`}
                            <HiOutlineArrowRight size={16} />
                        </button>
                    </>
                )}
            </RegistryHeader>

            <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
                {activeTab === "po_history" ? (
                    <div
                        ref={tableScrollRef}
                        className="relative overflow-auto border border-gray-200 bg-white flex-1"
                    >
                        <div className="min-w-max">
                            <Table
                                columns={[
                                    { key: "pO_No", label: "PO No" },
                                    { key: "pO_Date", label: "PO Date", render: (val) => formatDate(val) },
                                    { key: "supplier_Name", label: "Supplier Name" },
                                    {
                                        key: "total_Items",
                                        label: "Total Items",
                                        render: (val, row) => (
                                            <div
                                                className={`px-4 py-1 font-black text-[10px] uppercase tracking-widest inline-block ${expandedRows[row.pO_No || row.PO_No] ? 'bg-[#3F80F7] text-white' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}
                                            >
                                                {val || row.Total_Items || 0}
                                            </div>
                                        )
                                    },
                                    { key: "delivery_Date", label: "Delivery Date", render: (val) => formatDate(val) },
                                    { key: "delivery_Days", label: "Delivery Days" },
                                ]}
                                data={dataList.filter((item) =>
                                    !searchTerm ||
                                    item.supplier_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    item.pO_No?.toString().includes(searchTerm)
                                )}
                                tableClassName="text-[12px] font-bold"
                                rowIdField="pO_No"
                                expandedRows={expandedRows}
                                onExpand={handleExpandItems}
                                onEdit={(row) => handleEdit(row.pO_No || row.PO_No)}
                                onDelete={(row) => handleDelete(row.pO_No || row.PO_No)}
                                onPrint={handlePrint}
                                renderExpansion={(row) => {
                                    const poNo = row.pO_No || row.PO_No;
                                    const items = expandedData[poNo] || [];
                                    const isLoading = loadingDetails[poNo];

                                    if (isLoading) return <div className="p-12 flex items-center justify-center bg-gray-50 border-y border-gray-200"><Loader /></div>;
                                    if (items.length === 0) return <div className="p-8 text-center text-gray-400 font-bold uppercase tracking-widest bg-gray-50 border-y border-gray-100 text-[10px]">No specific line items found for this record.</div>;

                                    return (
                                        <div className="bg-white border-y border-gray-100">
                                            <div className="bg-white border border-gray-200 shadow-none">
                                                <div className="bg-[#3F80F7] px-6 py-3 flex justify-between items-center">
                                                    <h4 className="text-[11px] font-black text-white uppercase tracking-widest">Order Details: PO-{poNo}</h4>
                                                    <span className="bg-white/20 text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest">{items.length} Items</span>
                                                </div>
                                                <table className="w-full text-[11px] font-bold border-collapse">
                                                    <thead>
                                                        <tr className="bg-white text-gray-400 uppercase tracking-[1.5px] text-[9px] border-b border-gray-200 text-left">
                                                            <th className="px-6 py-4 border-r border-gray-100">DIE Number</th>
                                                            <th className="px-6 py-4 border-r border-gray-100">PROFILE</th>
                                                            <th className="px-6 py-4 border-r border-gray-100">SERIES</th>
                                                            <th className="px-6 py-4 border-r border-gray-100 text-right">Order Wt</th>
                                                            <th className="px-6 py-4 border-r border-gray-100 text-center">Width x Height</th>
                                                            <th className="px-6 py-4 border-r border-gray-100 text-center">Bloster</th>
                                                            <th className="px-6 py-4 text-center tracking-widest">ASSET</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {items.map((item, i) => {
                                                            const dieStr = item.dieNumber || "";
                                                            const lastIndex = dieStr.lastIndexOf('-');
                                                            const diePart = lastIndex !== -1 ? dieStr.substring(0, lastIndex) : dieStr;
                                                            const subPart = lastIndex !== -1 ? dieStr.substring(lastIndex + 1) : "";
                                                            const fileNameBase = `${poNo}_${diePart}${subPart ? '_' + subPart : ''}`;

                                                            return (
                                                                <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-6 py-3 font-black text-[#3F80F7] border-r border-gray-100 tracking-tight">{item.dieNumber}</td>
                                                                    <td className="px-6 py-3 text-gray-600 border-r border-gray-100">{item.profile_Name}</td>
                                                                    <td className="px-6 py-3 text-gray-400 border-r border-gray-100">{item.series_Name}</td>
                                                                    <td className="px-6 py-3 text-right font-black text-gray-800 border-r border-gray-100 uppercase">{item.orderWeight} KG</td>
                                                                    <td className="px-6 py-3 text-center border-r border-gray-100">
                                                                        <span className="text-gray-500 font-mono text-[14px]">{item.width_Height} MM</span>
                                                                    </td>
                                                                    <td className="px-6 py-3 text-center border-r border-gray-100">
                                                                        {item.is_Bloster ? (
                                                                            <span className="bg-gray-100 text-gray-700 px-2 py-1 text-[14px] font-black tracking-widest">#{item.bloster_No}</span>
                                                                        ) : (
                                                                            <span className="text-gray-200 text-[14px] font-black uppercase">N/A</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-3 text-center">
                                                                        <div className="flex items-center justify-center gap-3">
                                                                            {item.is_Pdf_Upload && (
                                                                                <a
                                                                                    href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoPDFFiles/${fileNameBase}.pdf`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="px-2 py-1 bg-gray-50 text-[#3F80F7] border border-gray-200 hover:bg-white font-black text-[9px] uppercase tracking-widest"
                                                                                >
                                                                                    DWG FILE
                                                                                </a>
                                                                            )}
                                                                            {item.is_Image_Upload && (
                                                                                <a
                                                                                    href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoImageFiles/${fileNameBase}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="px-2 py-1 bg-gray-50 text-[#3F80F7] border border-gray-200 hover:bg-white font-black text-[9px] uppercase tracking-widest"
                                                                                >
                                                                                    IMAGE DATA
                                                                                </a>
                                                                            )}
                                                                            {!item.is_Pdf_Upload && !item.is_Image_Upload && (
                                                                                <span className="text-gray-300 text-[9px] uppercase font-black tracking-widest opacity-40">Zero Assets</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden">
                        <PendingInwardTab
                            selectedItems={selectedItems}
                            setSelectedItems={setSelectedItems}
                            isInwardModalOpen={isInwardModalOpen}
                            setIsInwardModalOpen={setIsInwardModalOpen}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                loading={loading}
            />

            {errorModalOpen && (
                <ErrorModal
                    message={errorMessage}
                    subMessage={errorSubMessage}
                    onClose={() => setErrorModalOpen(false)}
                />
            )}
        </div>
    );
};

export default DiePurchaseOrderMaster;
